import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './shared/services/auth.service';
import { SharedModule } from '../shared/shared.module';
import { AppConfigService } from '../core/services/app-config.service';
import { AppConfigurationsKeys } from '../shared/consts/appConfigurations-consts';
import { LanguageInitializerService } from '../core/services/language-initializer.service';
import { LanguageViewModel } from '@/app/proxy/proxies';
import { MixpanelTrackerService } from '../core/analytics';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.less',
})
export class AuthComponent implements OnInit, OnChanges {
  logoPath = 'doktar-logo.png';

  languages: LanguageViewModel[] = [];
  selectedLanguage: LanguageViewModel | undefined;

  welcomeVideoUrl = '';
  termsOfUseLink = '';
  privacyPolicyLink = '';

  footer_texts = [
    { text: 'doktar_app.agreements.terms', link: this.termsOfUseLink },
    { text: 'doktar_app.agreements.privacy', link: this.privacyPolicyLink },
    { text: 'doktar_app.agreements.docs', link: '/' },
    { text: 'doktar_app.agreements.help', link: '/' },
  ];
  constructor(
    private _router: Router,
    private _authService: AuthService,
    private _appConfigService: AppConfigService,
    private _languageInitializerService: LanguageInitializerService,
    private _mixpanelTracker: MixpanelTrackerService
  ) {
    const session = this._authService.getCurrentSession();
    if (session) {
      this._router.navigateByUrl(window.location.hostname.includes('sustainability.doktar') ? '/csi' : '/home');
    }
  }

  async ngOnInit(): Promise<void> {
    if (this._appConfigService.hasConfigKey(AppConfigurationsKeys.welcomeVideoUrlWebKey)) {
      this.welcomeVideoUrl = this._appConfigService.welcomeVideoUrl;
    }
    this.termsOfUseLink = this._appConfigService.agreements?.en.terms;
    this.privacyPolicyLink = this._appConfigService.agreements?.en.terms;

    await this._languageInitializerService.initializeLanguage();
    
    this.languages = this._languageInitializerService.getLanguages();
    
    this.setSelectedLanguageFromCurrent();

    // Track Onboarding Viewed event
    this._mixpanelTracker.trackOnboardingViewed();
  }

  private setSelectedLanguageFromCurrent(): void {
    const currentLanguageId = this.getCurrentLanguage();
    if (currentLanguageId) {
      const foundLanguage = this.languages.find((lang) => lang.id === currentLanguageId);
      if (foundLanguage) {
        this.selectedLanguage = foundLanguage;
        return;
      }
    }
    
    // Fallback 1: If current language not found, try to find English
    const englishLanguage = this.languages.find((lang) => lang.isO1 === 'en');
    if (englishLanguage) {
      this.selectedLanguage = englishLanguage;
      return;
    }
    
    // Fallback 2: If English not found, use the first available language
    if (this.languages.length > 0) {
      this.selectedLanguage = this.languages[0];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['welcomeVideoUrl']) {
      this.welcomeVideoUrl = changes['welcomeVideoUrl'].currentValue;
    }
    if (changes['termsOfUseLink']) {
      this.termsOfUseLink = changes['termsOfUseLink'].currentValue;
    }
    if (changes['privacyPolicyLink']) {
      this.privacyPolicyLink = changes['privacyPolicyLink'].currentValue;
    }
  }

  onRegister() {
    this._router.navigate(['/auth/register']);
  }

  onLogin() {
    this._router.navigate(['/auth/login']);
  }

  onLanguageChange(event: { value: LanguageViewModel }) {
    const language = event?.value;
    
    if (!language || !language.id) {
      return;
    }
    
    this._languageInitializerService.setLanguage(language.id);
  }

  getCurrentLanguage(): string | null {
    try {
      return this._languageInitializerService.getCurrentLanguageGuid();
    } catch {
      return null;
    }
  }
}
